/**
 * Drives a multipart upload from wherever it currently stands to a finished object in R2.
 *
 * The same function serves a fresh upload and a resumed one, because after the first response they
 * are the same thing: a list of parts still missing, a part size to cut at, and a byte count R2
 * already holds. A fresh upload just happens to start with none of them uploaded.
 *
 * The loop is deliberately driven by the server rather than by a client-side ledger. R2 is the only
 * thing that actually knows which parts landed, so after each batch the next one is fetched rather
 * than derived — and that same call is what recovers a batch whose presigned URLs expired mid-way,
 * so there is one code path instead of a separate expiry branch that would almost never run and
 * would therefore almost never be right.
 *
 * It takes its two API calls as parameters instead of making them: fetching belongs in a thunk (see
 * `audioThunks`), and keeping it out of here is what lets the sequencing be reasoned about on its
 * own.
 */

import {AudioUpload, AudioUploadPart, AudioUploadPartsResponse} from '../backend-types/transcribe-api';
import {ApiError, isApiError} from './apiError';
import {bytesInParts, uploadPartBatch} from './multipartUpload';
import {toUploadProgress, UploadProgress} from './r2Upload';

/**
 * How many times `/complete` may answer "parts are still missing" before we stop.
 *
 * More than one because a part can be reported as landed and still not be visible to the manifest
 * immediately; bounded because a client that loops forever on this is uploading into a hole.
 */
const MAX_COMPLETE_ATTEMPTS = 3;

export interface ResumableUploadApi {
  /** `GET /audio/:id/parts` — what R2 is still missing, and the URLs to send it with. */
  fetchParts: () => Promise<AudioUploadPartsResponse>;
  /** `POST /audio/:id/complete` — rejects with an {@link ApiError} carrying `missingParts` on 400. */
  complete: () => Promise<AudioUpload>;
}

export interface RunResumableUploadOptions {
  file: File;
  /** Exactly what the server said. Never a local constant — uneven parts corrupt the object. */
  partSize: number;
  partCount: number;
  /** The batch to push first: from `/upload-url` on a fresh upload, from `/parts` on a resume. */
  parts: AudioUploadPart[];
  /** What R2 already holds, so a resumed bar starts where it left off instead of at zero. */
  uploadedParts: number;
  uploadedBytes: number;
  api: ResumableUploadApi;
  onProgress: (progress: UploadProgress) => void;
  /** Called before each `/complete` attempt — the stitch is not instant on a large file. */
  onFinalizing: () => void;
  signal?: AbortSignal;
}

/** Resolves with the `ready` upload record. */
export async function runResumableUpload({
  file,
  partSize,
  partCount,
  parts,
  uploadedParts,
  uploadedBytes,
  api,
  onProgress,
  onFinalizing,
  signal,
}: RunResumableUploadOptions): Promise<AudioUpload> {
  const total = file.size;

  let pending = parts;
  let doneParts = uploadedParts;
  // Only ever set from bytes the server has confirmed, or from a batch that finished. In-flight
  // bytes are added on top for display and never folded in, so a failed batch cannot leave the bar
  // permanently ahead of what R2 actually has.
  let confirmedBytes = Math.min(uploadedBytes, total);

  onProgress(toUploadProgress(confirmedBytes, total));

  const adoptServerState = (response: AudioUploadPartsResponse) => {
    pending = response.upload.parts;
    doneParts = response.upload.uploadedParts;
    confirmedBytes = Math.min(response.upload.uploadedBytes, total);
    onProgress(toUploadProgress(confirmedBytes, total));
  };

  const pushPendingBatches = async () => {
    while (pending.length > 0) {
      const batch = pending;
      const batchBytes = bytesInParts(file, partSize, batch);
      const base = confirmedBytes;

      await uploadPartBatch({
        file,
        partSize,
        parts: batch,
        signal,
        onBatchProgress: loaded => onProgress(toUploadProgress(base + loaded, total)),
      });

      // Only reached when every part in the batch is in R2 — `uploadPartBatch` throws otherwise.
      confirmedBytes = Math.min(base + batchBytes, total);
      doneParts += batch.length;
      onProgress(toUploadProgress(confirmedBytes, total));

      if (doneParts >= partCount) {
        return;
      }

      adoptServerState(await api.fetchParts());
    }
  };

  await pushPendingBatches();

  for (let attempt = 1; ; attempt += 1) {
    onFinalizing();

    try {
      return await api.complete();
    } catch (error) {
      const incomplete = isApiError(error)
        && error.status === 400
        && Array.isArray((error as ApiError).missingParts);

      if (!incomplete || attempt >= MAX_COMPLETE_ATTEMPTS) {
        throw error;
      }

      // Whatever R2 is missing, ask for it properly rather than trusting the 50 the error listed —
      // `missingCount` can exceed what it carries, and `/parts` hands back signed URLs the error
      // does not.
      adoptServerState(await api.fetchParts());
      await pushPendingBatches();
    }
  }
}
